import * as z from 'zod';

import type { Edits } from '../utils';
import type { RevisionInfo, RevisionSemanticChange } from './revision';

/**
 * History of a content branch containing the semantic changes for each of
 * the revisions created in the lifetime of branch.
 */
export interface ContentBranchHistory {
    history: ContentBranchHistoryItem[];
    next?: {
        page: RevisionInfo['id'];
    };
}

export type ContentBranchHistoryItem = {
    revision: RevisionInfo;
    changes: RevisionSemanticChange[];
    more?: number;
};

/**
 * ID of the default branch in any content
 */
export const CONTENT_DEFAULT_BRANCH = 'main';

/**
 * A branch is a named lightweight pointer to a revision.
 */
export interface ContentBranch {
    id: string;

    /**
     * ID of the active revision.
     */
    revision: string;

    /**
     * Last base revision from main when merging with conflicts.
     */
    revisionMergedAncestor?: string;

    /**
     * ID of the revision resulting from the merge of the branch.
     */
    revisionMerged?: string;

    /**
     * Who edited the branch over time.
     */
    edits: Edits;

    /**
     * ID of the origin branch and its active revision when the branch was created.
     */
    origin?: ContentBranchOrigin;
}

/**
 * Origin of a branch.
 */
export interface ContentBranchOrigin {
    /**
     * ID of the origin branch.
     */
    id: string;

    /**
     * ID of the origin branch's active revision at the time of the creation of the branch.
     */
    revision: string;
}
export interface ContentBranchMergeResult {
    revisionId: string;
    conflicts: boolean;

    baseRevisionId: string;
    headRevisionId: string;
    ancestorRevisionId: string;
}

export const ContentBranchMergeStrategySchema = z
    .enum(['prefer-head', 'annotate-conflicts'])
    .optional();
export type ContentBranchMergeStrategy = z.infer<typeof ContentBranchMergeStrategySchema>;

export const ContentBranchMergeRequestSchema = z
    .object({
        strategy: ContentBranchMergeStrategySchema,
    })
    .optional();
export type ContentBranchMergeRequest = z.infer<typeof ContentBranchMergeRequestSchema>;
